import { LeadAssignmentStatus, LeadAssignmentStatusType } from "types/declarations/enums.ts";
import { models } from "types/api/viewModels.ts";
import { splitPascalCase } from "utils/string.ts";
import { UnavailableDataPlaceholderText } from "constants/text.ts";

export const UnassignedLeadText = "Unassigned";
export type AssignedToPersonNameLookup = Record<string, string>;
export type LeadDateFilterOption = "all" | "last7Days" | "last30Days" | "last365Days";

const leadDateFilterDays: Record<Exclude<LeadDateFilterOption, "all">, number> = {
	last7Days: 7,
	last30Days: 30,
	last365Days: 365
};

export const leadIsClosed = (leadStatus: LeadAssignmentStatusType | undefined): boolean => {
	const closedLeads = [
		LeadAssignmentStatus.Sold,
		LeadAssignmentStatus.Declined,
		LeadAssignmentStatus.Lost,
		LeadAssignmentStatus.Unresponsive,
		LeadAssignmentStatus.Locked
	];

	return !!leadStatus && closedLeads.includes(leadStatus);
};

export const getLeadFullName = (lead: models["LeadAssignmentViewModel"]) =>
	[lead.lead?.firstName, lead.lead?.lastName].filter(Boolean).join(" ");

const isValidDate = (dateString: string | null | undefined) => {
	if (!dateString) return false;
	return !Number.isNaN(new Date(dateString).getTime());
};

export const leadMatchesDateFilter = (
	lead: models["LeadAssignmentViewModel"],
	selectedDateFilter: LeadDateFilterOption,
	now: Date = new Date()
) => {
	if (selectedDateFilter === "all") {
		return true;
	}

	if (!isValidDate(lead.createdAt)) {
		return false;
	}

	const createdAt = new Date(lead.createdAt as string);
	const daysToInclude = leadDateFilterDays[selectedDateFilter];

	const startDate = new Date(now);
	startDate.setHours(0, 0, 0, 0);
	startDate.setDate(startDate.getDate() - (daysToInclude - 1));

	const endDate = new Date(now);
	endDate.setHours(23, 59, 59, 999);

	return createdAt >= startDate && createdAt <= endDate;
};

export const isLeadUnassigned = (lead: models["LeadAssignmentViewModel"]) => !lead.assignedToUserId;

export const getAssignedToPersonLookupKey = (assignedToUserId: string | null | undefined) =>
	assignedToUserId?.trim().toLowerCase() ?? "";

export const getAssignedToPersonName = (
	lead: models["LeadAssignmentViewModel"],
	assignedToPersonNameLookup: AssignedToPersonNameLookup = {}
) => {
	if (lead.assignedToPersonName?.trim()) {
		return lead.assignedToPersonName;
	}

	const assignedToUserLookupKey = getAssignedToPersonLookupKey(lead.assignedToUserId);

	return (assignedToUserLookupKey && assignedToPersonNameLookup[assignedToUserLookupKey]) || UnassignedLeadText;
};

export const formatLeadDate = (dateString: string | null | undefined) => {
	if (!dateString || !isValidDate(dateString)) return UnavailableDataPlaceholderText;

	const isoDateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString);
	if (isoDateMatch) {
		const year = isoDateMatch[1];
		const month = isoDateMatch[2];
		const day = isoDateMatch[3];

		if (year && month && day) {
			return `${month}/${day}/${year.slice(-2)}`;
		}
	}

	const date = new Date(dateString);
	return `${String(date.getUTCMonth() + 1).padStart(2, "0")}/${String(date.getUTCDate()).padStart(2, "0")}/${String(
		date.getUTCFullYear()
	).slice(-2)}`;
};

export const getLeadStatusLabel = (status: LeadAssignmentStatusType | undefined) => {
	if (!status) return "";

	return splitPascalCase(status).join(" ");
};

export const getLeadStatusDisplay = (lead: models["LeadAssignmentViewModel"]) => {
	const statusLabel = getLeadStatusLabel(lead.status);

	if (!statusLabel) return UnavailableDataPlaceholderText;

	if (lead.status === LeadAssignmentStatus.FollowUp && lead.followUpDate) {
		return `${statusLabel} - ${formatLeadDate(lead.followUpDate)}`;
	}

	return statusLabel;
};

export const getLeadDateSortValue = (dateString: string | null | undefined) => {
	if (!dateString || !isValidDate(dateString)) return 0;

	return new Date(dateString).getTime();
};
